import React, { useState, useEffect, useRef } from 'react';
import Layout from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Mail, ArrowLeft, Eye, Send, Loader2, Save, Code, Wand2, RotateCcw, AlertTriangle } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import RichTextEditor from '../../components/common/RichTextEditor';

const API = process.env.REACT_APP_BACKEND_URL;

const EmailTemplatesPage = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editTemplate, setEditTemplate] = useState(null);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewKey, setPreviewKey] = useState(null); // remember which template is open in the preview modal so the toggle can refetch
  const [previewUseRealUser, setPreviewUseRealUser] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(null);
  const [editorTab, setEditorTab] = useState('visual');
  const [resetting, setResetting] = useState(false);
  const [templateSearch, setTemplateSearch] = useState('');
  const richEditorRef = useRef(null);
  const htmlTextareaRef = useRef(null);
  const [visualEditorKey, setVisualEditorKey] = useState(0);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchTemplates(); }, []);

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/api/email/templates`, { headers });
      setTemplates(res.data);
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to load templates', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async (templateKey, useRealUser = false) => {
    setPreviewKey(templateKey);
    setPreviewUseRealUser(useRealUser);
    setPreviewLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/email/templates/${templateKey}/preview`,
        {},
        { headers, params: { use_real_user: useRealUser } }
      );
      setPreviewHtml(res.data);
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to generate preview', variant: 'destructive' });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSave = async () => {
    if (!editTemplate) return;
    setSaving(true);
    try {
      await axios.put(`${API}/api/email/templates/${editTemplate.template_key}`, {
        subject: editTemplate.subject,
        html_body: editTemplate.html_body,
        is_active: editTemplate.is_active,
      }, { headers });
      toast({ title: 'Saved', description: `Template "${editTemplate.name}" updated.` });
      setEditTemplate(null);
      fetchTemplates();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to save template', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async (templateKey) => {
    setSendingTest(templateKey);
    try {
      const res = await axios.post(`${API}/api/email/templates/${templateKey}/send-test`, {}, { headers });
      toast({ title: 'Sent', description: res.data.message });
    } catch (err) {
      toast({ title: 'Failed', description: err.response?.data?.detail || 'Failed to send test', variant: 'destructive' });
    } finally {
      setSendingTest(null);
    }
  };

  const openEditor = (template) => {
    // Fresh copy so cancel discards changes. Force the Visual tab and remount
    // the TipTap editor so it picks up the template body cleanly.
    setEditTemplate({ ...template });
    setEditorTab('visual');
    setVisualEditorKey((k) => k + 1);
  };

  const insertVariable = (variableName) => {
    if (!editTemplate) return;
    const token = `{{${variableName}}}`;
    if (editorTab === 'visual' && richEditorRef.current) {
      // TipTap: insert at the current cursor position
      richEditorRef.current.chain().focus().insertContent(token).run();
      return;
    }
    // HTML tab — insert at the textarea's caret position
    const ta = htmlTextareaRef.current;
    if (ta && typeof ta.selectionStart === 'number') {
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const before = editTemplate.html_body.slice(0, start);
      const after = editTemplate.html_body.slice(end);
      const next = before + token + after;
      setEditTemplate({ ...editTemplate, html_body: next });
      // Restore caret after the inserted token on next tick
      requestAnimationFrame(() => {
        try {
          ta.focus();
          const pos = start + token.length;
          ta.setSelectionRange(pos, pos);
        } catch (_) { /* no-op */ }
      });
    } else {
      setEditTemplate({ ...editTemplate, html_body: (editTemplate.html_body || '') + token });
    }
  };

  const resetToDefault = async () => {
    if (!editTemplate) return;
    // Guard — this is destructive within the dialog but recoverable via Cancel
    if (!window.confirm(
      `Reset "${editTemplate.name}" to its factory default? Your unsaved edits in this dialog will be discarded (nothing is written to the server until you press Save).`
    )) return;
    setResetting(true);
    try {
      const res = await axios.get(`${API}/api/email/templates/${editTemplate.template_key}/default`, { headers });
      setEditTemplate({
        ...editTemplate,
        subject: res.data.subject,
        html_body: res.data.html_body,
      });
      // Force TipTap to remount so it picks up the new content
      setVisualEditorKey((k) => k + 1);
      toast({ title: 'Reset', description: 'Template restored to seeded default. Press Save to persist.' });
    } catch (err) {
      toast({
        title: 'No default available',
        description: err.response?.data?.detail || 'This template has no seeded default to reset to.',
        variant: 'destructive',
      });
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      </Layout>
    );
  }

  const filteredTemplates = templateSearch.trim()
    ? templates.filter(t => {
        const q = templateSearch.toLowerCase();
        return (
          (t.name || '').toLowerCase().includes(q) ||
          (t.template_key || '').toLowerCase().includes(q) ||
          (t.description || '').toLowerCase().includes(q) ||
          (t.subject || '').toLowerCase().includes(q)
        );
      })
    : templates;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto py-6 px-4 space-y-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/tools/email')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Mail className="w-6 h-6 text-teal-600" />
              Email Templates
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Edit email content for different events. Click a template on the left to edit.
            </p>
          </div>
        </div>

        {/* Two-pane master-detail. Left = compact scrollable template list with
            search; right = full inline editor for the currently selected
            template. Replaces the previous long-scroll cards + edit modal so
            22+ templates stay browsable without vertical scroll fatigue.
            `h-[calc(100vh-220px)]` on the wrapper gives both panes a bounded
            height so the inner `overflow-y-auto` on the list actually kicks
            in — otherwise the pane grows to fit all 22 rows and the whole
            page scrolls, defeating the layout. */}
        <div className="flex gap-4 items-stretch h-[calc(100vh-220px)] min-h-[600px]">
          {/* LEFT PANE — template list */}
          <div className="w-96 shrink-0 flex flex-col border border-gray-200 rounded-lg bg-white overflow-hidden">
            <div className="p-3 border-b border-gray-200 bg-gray-50">
              <Input
                placeholder="Search templates…"
                value={templateSearch}
                onChange={(e) => setTemplateSearch(e.target.value)}
                className="h-8 text-sm"
                data-testid="template-search-input"
              />
              <p className="text-[11px] text-gray-500 mt-1.5">
                {filteredTemplates.length} of {templates.length} template{templates.length === 1 ? '' : 's'}
              </p>
            </div>
            <div className="flex-1 overflow-y-auto">
              {filteredTemplates.length === 0 ? (
                <div className="p-6 text-center text-sm text-gray-400">
                  No templates match &ldquo;{templateSearch}&rdquo;
                </div>
              ) : (
                filteredTemplates.map(t => {
                  const isSelected = editTemplate?.template_key === t.template_key;
                  return (
                    <button
                      key={t.template_key}
                      type="button"
                      onClick={() => openEditor(t)}
                      className={`w-full text-left px-3 py-2.5 border-b border-gray-100 hover:bg-teal-50/60 transition-colors ${
                        isSelected ? 'bg-teal-50 border-l-4 border-l-teal-500' : 'border-l-4 border-l-transparent'
                      }`}
                      data-testid={`template-${t.template_key}`}
                    >
                      <div className="flex items-center gap-2">
                        <h3 className={`text-sm font-medium truncate ${isSelected ? 'text-teal-800' : 'text-gray-800'}`}>
                          {t.name}
                        </h3>
                        {!t.is_active && (
                          <Badge variant="outline" className="text-[10px] text-gray-400 border-gray-200 shrink-0">
                            Off
                          </Badge>
                        )}
                      </div>
                      <p className="text-[11px] text-gray-500 mt-0.5 line-clamp-2">{t.description}</p>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* RIGHT PANE — editor for selected template */}
          <div className="flex-1 border border-gray-200 rounded-lg bg-white min-w-0">
            {!editTemplate ? (
              <div className="flex flex-col items-center justify-center h-full py-24 text-gray-400">
                <Mail className="w-12 h-12 mb-3 text-gray-300" />
                <p className="text-sm">Select a template on the left to view or edit it.</p>
              </div>
            ) : (
              <div className="flex flex-col h-full">
                {/* Right-pane header — name + subject + top-right actions */}
                <div className="p-4 border-b border-gray-200 bg-gray-50">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <h2 className="text-base font-semibold text-gray-900 truncate">{editTemplate.name}</h2>
                        <Badge
                          variant="outline"
                          className={editTemplate.is_active ? 'text-green-700 border-green-200 bg-green-50 text-[10px]' : 'text-gray-400 border-gray-200 text-[10px]'}
                        >
                          {editTemplate.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">{editTemplate.description}</p>
                      <p className="text-[11px] text-gray-400 mt-1 font-mono">{editTemplate.template_key}</p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handlePreview(editTemplate.template_key)}
                        className="h-8 text-xs"
                        data-testid={`preview-${editTemplate.template_key}`}
                      >
                        <Eye className="w-3.5 h-3.5 mr-1" />
                        Preview
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSendTest(editTemplate.template_key)}
                        disabled={sendingTest === editTemplate.template_key || !editTemplate.is_active}
                        className="h-8 text-xs"
                        data-testid={`send-test-${editTemplate.template_key}`}
                      >
                        {sendingTest === editTemplate.template_key ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
                        Test
                      </Button>
                      <Switch
                        checked={editTemplate.is_active}
                        onCheckedChange={(v) => setEditTemplate({ ...editTemplate, is_active: v })}
                        data-testid="template-active-toggle"
                      />
                    </div>
                  </div>
                </div>

                {/* Right-pane body — subject, editor tabs, variables */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  <div>
                    <Label>Subject Line</Label>
                    <Input
                      value={editTemplate.subject}
                      onChange={(e) => setEditTemplate({ ...editTemplate, subject: e.target.value })}
                      className="mt-1"
                      data-testid="template-subject-input"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label>Body</Label>
                      <Tabs
                        value={editorTab}
                        onValueChange={(next) => {
                          if (editorTab === 'html' && next === 'visual') {
                            setVisualEditorKey((k) => k + 1);
                          }
                          setEditorTab(next);
                        }}
                        className="w-auto"
                      >
                        <TabsList className="h-8">
                          <TabsTrigger value="visual" className="text-xs h-6" data-testid="template-editor-tab-visual">
                            <Wand2 className="w-3 h-3 mr-1" /> Visual
                          </TabsTrigger>
                          <TabsTrigger value="html" className="text-xs h-6" data-testid="template-editor-tab-html">
                            <Code className="w-3 h-3 mr-1" /> HTML
                          </TabsTrigger>
                        </TabsList>
                      </Tabs>
                    </div>

                    {editorTab === 'visual' ? (
                      <>
                        <div className="border border-gray-200 rounded-md" data-testid="template-editor-visual-wrap">
                          <RichTextEditor
                            key={visualEditorKey}
                            content={editTemplate.html_body || ''}
                            onChange={(html) => setEditTemplate((prev) => (prev ? { ...prev, html_body: html } : prev))}
                            onEditorReady={(ed) => { richEditorRef.current = ed; }}
                            placeholder="Compose your email…"
                          />
                        </div>
                        <div className="flex items-start gap-2 mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-[11px] text-amber-800">
                          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                          <span>Visual mode may simplify complex inline styles (gradients, custom buttons). Switch to <b>HTML</b> for pixel-level control, or use <b>Reset to default</b> if the layout is broken.</span>
                        </div>
                      </>
                    ) : (
                      <Textarea
                        ref={htmlTextareaRef}
                        value={editTemplate.html_body}
                        onChange={(e) => setEditTemplate({ ...editTemplate, html_body: e.target.value })}
                        rows={16}
                        className="mt-1 font-mono text-xs"
                        data-testid="template-body-input"
                      />
                    )}
                  </div>

                  {editTemplate.variables?.length > 0 && (
                    <div>
                      <Label className="text-xs text-gray-500">Available Variables — click to insert at cursor</Label>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {editTemplate.variables.map(v => (
                          <button
                            type="button"
                            key={v}
                            onClick={() => insertVariable(v)}
                            className="text-xs bg-teal-50 text-teal-700 px-2 py-0.5 rounded border border-teal-200 hover:bg-teal-100 hover:border-teal-300 transition-colors font-mono cursor-pointer"
                            data-testid={`template-insert-var-${v}`}
                            title={`Insert {{${v}}} at cursor`}
                          >
                            {`{{${v}}}`}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Right-pane footer — actions pinned at the bottom */}
                <div className="p-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between gap-2">
                  <Button
                    variant="outline"
                    onClick={resetToDefault}
                    disabled={resetting || saving}
                    className="text-xs"
                    data-testid="template-reset-default-btn"
                  >
                    {resetting ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5 mr-2" />}
                    Reset to default
                  </Button>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setEditTemplate(null)} data-testid="template-close-btn">
                      Close
                    </Button>
                    <Button onClick={handleSave} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="save-template-btn">
                      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Save Template
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Preview Dialog — kept as a modal since it renders arbitrary email HTML */}
        <Dialog
          open={!!previewHtml}
          onOpenChange={(open) => {
            if (!open) {
              setPreviewHtml(null);
              setPreviewKey(null);
              setPreviewUseRealUser(false);
            }
          }}
        >
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-teal-600" />
                Email Preview
              </DialogTitle>
            </DialogHeader>
            {previewHtml && (
              <div className="space-y-3 py-2">
                {/* Real-user toggle — catches template-vs-data mismatches like
                    the "your paid plan expires" bug where plan_name silently
                    leaked as the literal "paid"/"free" flag. */}
                <div className="flex items-center justify-between gap-3 p-3 rounded-md border bg-slate-50">
                  <div className="min-w-0">
                    <Label className="text-sm font-medium text-slate-800" htmlFor="preview-real-user">
                      Use real user data
                    </Label>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Substitutes a random non-admin user&apos;s real name, plan, expiry etc.
                      Best for spotting bad mappings before deploy.
                    </p>
                  </div>
                  <Switch
                    id="preview-real-user"
                    data-testid="preview-use-real-user-toggle"
                    checked={previewUseRealUser}
                    disabled={previewLoading || !previewKey}
                    onCheckedChange={(v) => previewKey && handlePreview(previewKey, v)}
                  />
                </div>
                {previewHtml.preview_source && (
                  <div
                    className={`text-xs px-3 py-2 rounded-md border ${
                      previewHtml.preview_source.mode === 'real'
                        ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                        : 'bg-amber-50 border-amber-200 text-amber-900'
                    }`}
                    data-testid="preview-source-badge"
                  >
                    {previewHtml.preview_source.mode === 'real' ? (
                      <>
                        Rendered with real data from{' '}
                        <span className="font-mono">{previewHtml.preview_source.email}</span>
                        {previewHtml.preview_source.is_paid ? ' · paid' : ' · free'} ·{' '}
                        plan: <span className="font-medium">{previewHtml.preview_source.plan_name}</span>
                      </>
                    ) : (
                      <>Rendered with sample placeholder data{previewHtml.preview_source.note ? ` — ${previewHtml.preview_source.note}` : ''}.</>
                    )}
                  </div>
                )}
                <div>
                  <Label className="text-xs text-gray-500">Subject</Label>
                  <p className="text-sm font-medium text-gray-800 mt-0.5" data-testid="preview-subject">
                    {previewHtml.subject}
                  </p>
                </div>
                <div className="border rounded-lg p-4 bg-white relative" data-testid="preview-html-container">
                  {previewLoading && (
                    <div className="absolute inset-0 bg-white/70 flex items-center justify-center rounded-lg">
                      <Loader2 className="w-5 h-5 animate-spin text-teal-600" />
                    </div>
                  )}
                  <div dangerouslySetInnerHTML={{ __html: previewHtml.html }} />
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default EmailTemplatesPage;
